import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { StatusCode } from "api/protocols";
import Checkbox from "components/form/checkbox/Checkbox";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Page from "components/page/Page";
import { Select } from "components/select/index";
import { isEqualWith } from "lodash";
import { useTranslation } from "react-i18next";
import "./accounts.scss";
import { IPostAccount } from "api/rpc/2024-04/facilityAdmin/client/accounts";
import { PostAccount } from "api/rpc/2024-04/facilityAdmin/client/accounts/accounts";
import { GetAccountingReference, IAccount } from "api/rpc/2024-04/facilityAdmin/facility/accounting";
import { formatAccountingReferenceLabel } from "utils/formatAccountingReferenceLabel";
import { useAppSelector } from "hooks/redux";
import Toggle from "components/form/toggle/Toggle";

export interface IAccountingReferenceTypes {
  label: string;
  value: number | null;
}

const minimumDayInput = 1;
const maximumDayInput = 31;

export default function AccountNew() {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { Option } = Select;
  const { facilityStore } = useAppSelector(store => store);
  const backToAccountsUrl = `/admin/settings/accounts${location.search || ""}`;
  const [account, setAccount] = useState<IAccount>({
    title: "",
    currency: "cad",
    global: false,
    account_number: "",
    limit: "",
    limit_is_valid: false,
    accounting_reference_id: 0,
    auto_pay_anchor_date: null,
    auto_pay_balance: false,
    send_statement: false,
  });

  const [accountBeforeChanges, setAccountBeforeChanges] = useState<IAccount>({
    title: "",
    currency: "cad",
    global: false,
    account_number: "",
    limit: "",
    limit_is_valid: false,
    accounting_reference_id: 0,
    auto_pay_anchor_date: null,
    auto_pay_balance: false,
    send_statement: false,
  });
  const [accountingReferenceTypes, setAccountingReferenceTypes] = useState<IAccountingReferenceTypes[]>(undefined);

  useEffect(() => {
    if (facilityStore.teeSheetSettings) {
      setAccount(prev => ({
        ...prev,
        accounting_reference_id: facilityStore?.teeSheetSettings?.accounts_accounting_reference_id,
        auto_pay_anchor_date: facilityStore?.teeSheetSettings?.accounts_auto_pay_anchor_date,
        auto_pay_balance: !!facilityStore?.teeSheetSettings?.accounts_auto_pay_balance,
        send_statement: !!facilityStore?.teeSheetSettings?.accounts_send_statement,
      }));

      setAccountBeforeChanges(prev => ({
        ...prev,
        accounting_reference_id: facilityStore?.teeSheetSettings?.accounts_accounting_reference_id,
        auto_pay_anchor_date: facilityStore?.teeSheetSettings?.accounts_auto_pay_anchor_date,
        auto_pay_balance: !!facilityStore?.teeSheetSettings?.accounts_auto_pay_balance,
        send_statement: !!facilityStore?.teeSheetSettings?.accounts_send_statement,
      }));
    }
    void loadAccountingReferenceTypes();
  }, []);

  async function loadAccountingReferenceTypes() {
    const accountingResponse = await GetAccountingReference(
      {
        group: "accounts_receivable",
      },
      true,
    );

    if (accountingResponse.status !== StatusCode.OK) {
      return;
    }

    const accountingReferenceTypes: IAccountingReferenceTypes[] = [];

    accountingReferenceTypes.push({ label: t("None"), value: 0 });

    if (accountingResponse?.data?.length !== undefined && accountingResponse.data.length > 0) {
      const accountingReferenceTypesResponse = accountingResponse.data.map(
        (accountingReference: { title?: string; reference_number?: string | null; id?: number }) => {
          return {
            label: formatAccountingReferenceLabel(
              accountingReference.title ?? "",
              accountingReference.reference_number,
            ),
            value: accountingReference.id ?? 0,
          };
        },
      );

      accountingReferenceTypes.push(...accountingReferenceTypesResponse);
    }

    setAccountingReferenceTypes(accountingReferenceTypes);
  }

  function handleChange(event: any) {
    const id = event?.target?.id;
    const value = event?.target?.value;

    if (id === undefined || value === undefined) {
      return;
    }

    setAccount(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDropDownChange(id: any) {
    setAccount(prevState => ({ ...prevState, accounting_reference_id: id }));
  }

  function handleCheckboxChange(event: any) {
    const id = event?.target?.id;
    const value = event?.target?.value;

    if (id === undefined || value === undefined) {
      return;
    }

    const booleanValue = value === "true";

    setAccount(prevState => ({ ...prevState, [id]: !booleanValue }));
  }

  function handleNumberChange(event: any, propertyToChange: string, validFlagToChange: string) {
    const value = event?.target?.value;

    if (value === undefined) {
      return;
    }

    const valueCharacters = value.split("");
    setAccount(prevState => ({
      ...prevState,
      [propertyToChange]: value,
      [validFlagToChange]: valueCharacters.length > 0 && valueCharacters.every((v: any) => !Number.isNaN(Number(v))),
    }));
  }

  async function saveAccount() {
    if (account.title === "" || !account.limit_is_valid) {
      return;
    }

    const postAccount: IPostAccount = {
      title: account.title,
      currency: facilityStore?.facility?.default_currency_code ?? account.currency,
      global: account.global,
      account_number: account.account_number === "" ? undefined : account.account_number,
      limit: Number(account.limit),
      accounting_reference_id: account.accounting_reference_id === 0 ? null : account.accounting_reference_id,
      send_statement: account.send_statement,
      auto_pay_balance: account.auto_pay_balance,
      auto_pay_anchor_date: account.auto_pay_anchor_date,
    };

    const accountResponse = await PostAccount(postAccount, true);

    if (accountResponse.status !== StatusCode.OK) {
      return;
    }

    history.push(backToAccountsUrl);
  }

  function unsavedChangesExist() {
    if (accountBeforeChanges === undefined) {
      if (accountingReferenceTypes) {
        setAccountBeforeChanges(prev => ({ ...prev, account }));
      }
      return false;
    }

    return !isEqualWith(accountBeforeChanges, account);
  }

  function cancelUnsavedChanges() {
    setAccount(accountBeforeChanges);
  }

  return (
    <Page
      title={t("New Account")}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("Accounts"),
          url: backToAccountsUrl,
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveAccount,
        onCancel: cancelUnsavedChanges,
      }}
    >
      {accountingReferenceTypes ? (
        <Form>
          <Card>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Input value={account.title} label={t("Title")} id="title" onChange={handleChange} />
                  <Input
                    value={account.account_number}
                    id="account_number"
                    label={t("Account Number")}
                    onChange={handleChange}
                    tooltipText={t("Leave empty to auto generate a number")}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <Input
                    value={account.limit}
                    label={t("Limit")}
                    id={account.limit.length > 0 && !account.limit_is_valid ? "inputError" : ""}
                    onChange={(e: any) => handleNumberChange(e, "limit", "limit_is_valid")}
                  />
                  <Select
                    label={t("Accounting Reference")}
                    defaultValue={facilityStore?.teeSheetSettings?.accounts_accounting_reference_id}
                    onChange={handleDropDownChange}
                  >
                    {accountingReferenceTypes.map((type, index) => {
                      return (
                        <Option key={index} value={type.value} name={type.label}>
                          <span>{type.label}</span>
                        </Option>
                      );
                    })}
                  </Select>
                </FormLayout.Group>
                <FormLayout.Group>
                  <Checkbox
                    id="global"
                    size="medium"
                    value={account.global}
                    checked={account.global}
                    onChange={handleCheckboxChange}
                    label={t("This account can be used across all courses")}
                  ></Checkbox>
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Toggle
                    id="auto_pay_balance"
                    checked={account?.auto_pay_balance}
                    onChange={e => setAccount(prev => ({ ...prev, auto_pay_balance: !!e.target.checked }))}
                    label={t("Auto Pay Balance")}
                  />

                  <Toggle
                    id="send_statement"
                    checked={account?.send_statement}
                    onChange={e => setAccount(prev => ({ ...prev, send_statement: !!e.target.checked }))}
                    label={t("Send Statements")}
                  />
                </FormLayout.Group>

                {account?.auto_pay_balance ? (
                  <FormLayout.Group>
                    <Input
                      label={t("Auto Pay Date")}
                      type="number"
                      id="accounts_auto_pay_anchor_date"
                      value={
                        account?.auto_pay_anchor_date < minimumDayInput
                          ? ""
                          : account?.auto_pay_anchor_date > maximumDayInput
                          ? maximumDayInput
                          : account?.auto_pay_anchor_date
                      }
                      onChange={e =>
                        setAccount(prev => ({ ...prev, accounts_auto_pay_anchor_date: Number(e.target?.value) }))
                      }
                      prefix={t("Day of Month")}
                      error={account?.auto_pay_anchor_date === 0}
                    />
                  </FormLayout.Group>
                ) : null}
              </FormLayout>
            </Card.Section>
          </Card>
        </Form>
      ) : null}
    </Page>
  );
}
